import React, { useEffect, useState, useRef } from 'react';
import { get } from '../utils/request';
import MoveTo from 'moveto';
import PropTypes from 'prop-types';

import { CSSTransition } from 'react-transition-group';
import extractSearchString from '../utils/extractSearchString';
import { prepareCleanString, stringContainsUnsupportedCharacters } from '../utils/unsupportedCharactersUtils';

import Step1 from './RegisterDomain/Step1';
import Step2 from './RegisterDomain/Step2';
import Step3 from './RegisterDomain/Step3';

const AVAIL_API_VERSION = '1.0';
const AVAIL_API_URL = `https://availapi.prod.inz.net.nz/${AVAIL_API_VERSION}`;

const dataSet = document
  .getElementById('main')
  .getAttribute('data-search-domain');
const AVAILABILITY_API_URL = dataSet;

const moderatedDomains = [
  '.cri.nz',
  '.govt.nz',
  '.health.nz',
  '.iwi.nz',
  '.mil.nz',
  '.parliament.nz',
];

// order is important
const priorityDomain = [
  '.nz',
  '.co.nz',
  '.org.nz',
  '.net.nz',
  '.maori.nz',
  '.kiwi.nz',
  '.school.nz',
  '.ac.nz',
  '.geek.nz',
  '.gen.nz',
];



function RegisterDomain({
  stepOneContent,
  helperStepOne,
  helperStepTwo,
  helperStepThree,
  registrarFilters,
  isNewRegistry,
  contactLink,
  tlds,
  isJumpstarter,
  helperUnsupportedCharacters,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [domains, setDomains] = useState([]);
  // the term that was used to query the API
  const [query, setQuery] = useState('');
  // the full query including unsupported characters
  const [originalQuery, setOriginalQuery] = useState('');
  const [selectedDomain, setSelectedDomain] = useState(null);
  // used for Jumpstarter to check if the query has unsupported characters so a new popup can be displayed
  const [hasUnsupportedCharacters, setHasUnsupportedCharacters] = useState(false);
  // unavailable manage domain availability for step 2
  const [unavailable, setUnavailable] = useState(false);

  const StepTwo = useRef(null);
  const StepThree = useRef(null);

  const allowedTLDs = (tlds || '').split(',').map(tld => tld.trim()).filter(tld => !!tld);

  const selectDomain = value => {
    // We set the selected domain with the second-level
    setSelectedDomain(query + value);
    // Send explicit GTM event
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'domain_select', domain: query + value });

    const moveTo = new MoveTo({
      tolerance: 200,
      easing: 'easeOutQuart',
    });
    moveTo.move(StepThree.current);
  };

  // checkFilters is just checking if the filters are parse-able
  // and prevent the JS to break at the higher level
  const checkFilters = () => {
    let filters = [];

    try {
      filters = JSON.parse(registrarFilters);
    } catch (err) {
      console.warn(
        'something went wrong with loading the filters into the app',
        err,
      );
    }

    return filters;
  };

  const checkForAvailableDomains = domain => {
    return (
      domain.statuses.includes('AVAILABLE') &&
      (!domain.statuses.includes('BLOCKED') ||
        !domain.statuses.includes('BLOCKED_OVERRIDE'))
    );
  };

  // Check if there is a domain search in the URL and update the query accordingly
  useEffect(() => {
    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search);
    const domainFromURL = params.get('d');

    if (domainFromURL && typeof domainFromURL === 'string') {
      let searchString = extractSearchString(domainFromURL, isJumpstarter);
      setOriginalQuery(searchString);
      if (isJumpstarter) {
        const cleanedString = prepareCleanString(searchString)
        setHasUnsupportedCharacters(cleanedString.length !== searchString.length)
        searchString = cleanedString
      } 
        setQuery(searchString);
    } else {
      // This is to prevent XSS attack or others types of attack
      console.warn('The domain query need to be a string');
    }
  }, []);

  // Load the results from the domain availability API
  useEffect(() => {
    // Skip applying effect on first load
    let ignore = false;

    const fetchData = async () => {
      setError(false);
      setUnavailable(false);
      setLoading(true);
      setSelectedDomain(null);
      setDomains([]);

      const AVAILABILITY_SEARCH_URL = isNewRegistry
        ? `${AVAILABILITY_API_URL}?domains=`
        : `${AVAIL_API_URL}/availability?string=`;
      try {
        const result = await get(
          `${AVAILABILITY_SEARCH_URL}${query}.nz`,
        );

        setLoading(false);

        // If we've a set of allowed TLDs (JumpStarter), filter results to those
        // Using map & find to order them in the order provided by allowedTLDs
        if (allowedTLDs.length) {
          if (isNewRegistry) {
            result.data[0].results = allowedTLDs
              .map(tld => result.data[0].results.find(item => item.domainName === `${query}${tld}`))
              .filter(item => item !== undefined);
          } else {
            result.data = allowedTLDs
              .map(tld => result.data.find(item => item.domain === `${query}${tld}`))
              .filter(item => item !== undefined);
          }
        }

        // Check if there is an available domain before moving to step 2
        // Otherwise display error message
        if (
          (!isNewRegistry &&
            result.data.filter(
              domain => domain.status === 'Available',
            ).length > 0) ||
          (isNewRegistry &&
            result.data[0].results.filter(domain =>
              checkForAvailableDomains(domain),
            ).length > 0)
        ) {
          const data = isNewRegistry
            ? result.data[0].results
            : result.data;

          const filteredDomains = data.filter(domainData => {
            // Get the second level domain into an array
            const secondLevelDomain = isNewRegistry
              ? domainData.domainName.split('.')
              : domainData.domain.split('.');
            // remove the first one to only get the second level domain
            delete secondLevelDomain[0];

            // Add it to the list if it's not a moderated domain
            return !moderatedDomains.includes(
              secondLevelDomain.join('.'),
            );
          });

          // order result base on priority, priorityDomain
          filteredDomains.sort((firstEl, secondEl) => {
            // default order, last
            let firstElSortOrder = 99;
            let secondElSortOrder = 99;

            for (
              let sortOrder = 0;
              sortOrder < priorityDomain.length;
              sortOrder++
            ) {
              // set sort order if priority domain exists
              // override current sortOrder if sortOrder is lower
              if (isNewRegistry) {
                if (
                  firstEl.domainName.includes(
                    priorityDomain[sortOrder],
                  )
                ) {
                  firstElSortOrder = sortOrder;
                }
                if (
                  secondEl.domainName.includes(
                    priorityDomain[sortOrder],
                  )
                ) {
                  secondElSortOrder = sortOrder;
                }
              } else {
                if (
                  firstEl.domain.includes(priorityDomain[sortOrder])
                ) {
                  firstElSortOrder = sortOrder;
                }
                if (
                  secondEl.domain.includes(priorityDomain[sortOrder])
                ) {
                  secondElSortOrder = sortOrder;
                }
              }
            }
            // compare first and second element's sortOrder
            return firstElSortOrder - secondElSortOrder;
          });

          if (!ignore) setDomains(filteredDomains);

          // move to Step 2
          if (StepTwo.current) {
            const moveTo = new MoveTo({
              tolerance: 200,
              easing: 'easeOutQuart',
            });
            moveTo.move(StepTwo.current);
          }
        } else {
          setUnavailable(true);
          setLoading(false);
        }
      } catch (err) {
        console.warn('Error on domain lookup', err);
        setError(true);
        setLoading(false);
      }
    };

    if (query) {
      
      if (isJumpstarter && stringContainsUnsupportedCharacters(query)) {
        setQuery(prepareCleanString(query));
        setOriginalQuery(query);
        setHasUnsupportedCharacters(true);
      }
      // Send explicit GTM event
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'domain_lookup', domain: query });

      fetchData();
    } else {
      setSelectedDomain(null);
      setDomains([]);
    }

    return () => {
      ignore = true;
    };
  }, [query]);

  return (
    <div>
      <div className={`step-one ${isJumpstarter ? 'c-jumpstarter-step-one' : ''}`}>
        <Step1
          loading={loading}
          unavailable={unavailable}
          error={error}
          query={query}
          originalQuery={originalQuery}
          setQuery={setQuery}
          setOriginalQuery={setOriginalQuery}
          content={stepOneContent}
          helperStepOne={helperStepOne}
          isNewRegistry={isNewRegistry}
          isJumpstarter={isJumpstarter}
          hasUnsupportedCharacters={hasUnsupportedCharacters}
          helperUnsupportedCharacters={helperUnsupportedCharacters}
          setHasUnsupportedCharacters={setHasUnsupportedCharacters}
        />
      </div>
      <div className={`step-two ${isJumpstarter ? 'c-jumpstarter-step-two' : ''}`} ref={StepTwo}>
        <CSSTransition
          in={domains && domains.length > 0}
          appear
          key={domains && domains.length}
          timeout={400}
          classNames="opacity"
        >
          <Step2
            domains={domains}
            selectDomain={selectDomain}
            helperStepTwo={helperStepTwo}
            isNewRegistry={isNewRegistry}
            contactLink={contactLink}
            isJumpstarter={isJumpstarter}
          />
        </CSSTransition>
      </div>
      <div className={`step-three ${isJumpstarter ? 'c-jumpstarter-step-three' : ''}`} ref={StepThree}>
        <CSSTransition
          in={!!selectedDomain}
          appear
          key={selectedDomain}
          timeout={400}
          classNames="step__animation"
        >
          <Step3
            selectedDomain={selectedDomain}
            helperStepThree={helperStepThree}
            registrarFilters={checkFilters()}
            groupInTiers={!!allowedTLDs.length}
            isJumpstarter={isJumpstarter}
          />
        </CSSTransition>
      </div>
    </div>
  );
}

RegisterDomain.propTypes = {
  isNewRegistry: PropTypes.bool
};

export default RegisterDomain;
